import React, { useState, useRef, useEffect } from "react";
import "./GoogleDocsEditor.scss";

const GoogleDocsEditor = ({
  value = "",
  onChange = () => {},
  allowImages = true,
  allowPaste = true,
  allowPasteImages = true,
  disabled = false,
  placeholder = "Start typing...",
  height = "25rem",
}) => {
  const [content, setContent] = useState(value);
  const [activeFormats, setActiveFormats] = useState({});
  const [showDropdowns, setShowDropdowns] = useState({});
  const [selectedFont, setSelectedFont] = useState("Arial");
  const [selectedSize, setSelectedSize] = useState("14");
  const [selectedColor, setSelectedColor] = useState("#000000");
  const [selectedHighlight, setSelectedHighlight] = useState("#ffffff");
  const [align, setAlign] = useState("left");

  const editorRef = useRef(null);

  const fonts = ["Arial", "Helvetica", "Times New Roman", "Georgia", "Verdana"];

  const sizes = ["8","9","10","11","12","14","16","18","20","24","28","32"];

  const colors = [
    "#000000","#FFFFFF","#808080","#C0C0C0",
    "#FF0000","#800000","#FFC000","#FFFF00",
    "#92D050","#00B050","#00B0F0","#0070C0",
    "#002060","#7030A0","#FF99CC","#FF66CC",
    "#FFCC99","#FF9966","#FF6600","#CC3300",
    "#FF9999","#CC6666","#990000","#FFCC00",
    "#99CC00","#669900","#009900","#00CC99",
    "#00CCCC","#0099CC","#0066CC","#003399",
    "#3300CC","#6600CC","#9900CC","#CC00CC",
    "#FF00FF","#FF33FF","#FF3399","#FF0066",
    "#FF0033","#FF5050","#FFB6C1","#E6E6FA",
    "#ADD8E6","#87CEFA","#4682B4","#5F9EA0",
    "#7FFFD4","#40E0D0"
  ];

  const execCmd = (cmd, val = null) => {
    if (disabled) return;
    document.execCommand(cmd, false, val);
    updateFormats();
    if (editorRef.current) {
      const newContent = editorRef.current.innerHTML;
      setContent(newContent);
      onChange(newContent);
    }
  };

  const updateFormats = () => {
    if (!editorRef.current) return;
    setActiveFormats({
      bold: document.queryCommandState("bold"),
      italic: document.queryCommandState("italic"),
      underline: document.queryCommandState("underline"),
      strikethrough: document.queryCommandState("strikeThrough"),
      ul: document.queryCommandState("insertUnorderedList"),
      ol: document.queryCommandState("insertOrderedList"),
    });

    // Track alignment
    const justify = document.queryCommandValue("justifyFull") ||
                    document.queryCommandValue("justify") || "left";
    setAlign(justify.toLowerCase());
  };

  const handleChange = () => {
    if (editorRef.current) {
      const newContent = editorRef.current.innerHTML;
      onChange(newContent);
    }
  };

  const toggleDropdown = (name) => {
    setShowDropdowns((prev) => ({ ...prev, [name]: !prev[name] }));
  };
  const closeDropdowns = () => setShowDropdowns({});

  const insertImage = () => {
    if (!allowImages) return;
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          execCmd("insertHTML", `<img src="${e.target.result}" style="max-width:100%">`);
        };
        reader.readAsDataURL(file);
      }
    };
    input.click();
  };

  const handlePaste = (e) => {
    if (!allowPaste) {
      e.preventDefault();
      return;
    }
    const items = Array.from(e.clipboardData.items);
    const imageItem = items.find((item) => item.type.startsWith("image/"));
    if (imageItem) {
      if (!allowPasteImages) { e.preventDefault(); return; }
      e.preventDefault();
      const blob = imageItem.getAsFile();
      const reader = new FileReader();
      reader.onload = (e) => {
        execCmd("insertHTML", `<img src="${e.target.result}" style="max-width:100%">`);
      };
      reader.readAsDataURL(blob);
      return;
    }
    setTimeout(() => handleChange(), 0);
  };

  useEffect(() => {
    const handleClick = (e) => {
      if (!e.target.closest(".dropdown-wrapper")) closeDropdowns();
    };
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  useEffect(() => {
    if (editorRef.current && content !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = content;
    }
  }, [content]);

  return (
    <div className={`html-editor ${disabled ? "disabled" : ""}`}>
      <div className="editor-toolbar">
        {/* Font dropdown */}
        <div className="dropdown-wrapper">
          <button className="toolbar-btn toolbar-dropdown" onClick={() => toggleDropdown("font")}>
            <span>{selectedFont}</span><i className="fas fa-chevron-down"></i>
          </button>
          {showDropdowns.font && (
            <div className="dropdown-menu">
              {fonts.map((font) => (
                <button key={font} className="dropdown-item" style={{ fontFamily: font }}
                  onClick={() => { setSelectedFont(font); execCmd("fontName", font); closeDropdowns(); }}>
                  {font}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Size dropdown */}
        <div className="dropdown-wrapper">
          <button className="toolbar-btn" onClick={() => toggleDropdown("size")}>
            <span>{selectedSize}</span><i className="fas fa-chevron-down"></i>
          </button>
          {showDropdowns.size && (
            <div className="dropdown-menu">
              {sizes.map((size) => (
                <button key={size} className="dropdown-item"
                  onClick={() => {
                    const sizeMap = {8:"1",9:"1",10:"2",11:"2",12:"3",14:"4",16:"5",18:"5",20:"6",24:"7",28:"7",32:"7"};
                    setSelectedSize(size);
                    execCmd("fontSize", sizeMap[size] || "4");
                    closeDropdowns();
                  }}>
                  {size}px
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="toolbar-separator"></div>

        {/* Formatting buttons */}
        <button className={`toolbar-btn ${activeFormats.bold ? "active" : ""}`} onClick={() => execCmd("bold")}><i className="fas fa-bold"></i></button>
        <button className={`toolbar-btn ${activeFormats.italic ? "active" : ""}`} onClick={() => execCmd("italic")}><i className="fas fa-italic"></i></button>
        <button className={`toolbar-btn ${activeFormats.underline ? "active" : ""}`} onClick={() => execCmd("underline")}><i className="fas fa-underline"></i></button>
        <button className={`toolbar-btn ${activeFormats.strikethrough ? "active" : ""}`} onClick={() => execCmd("strikeThrough")}><i className="fas fa-strikethrough"></i></button>

        <div className="toolbar-separator"></div>

        {/* Text Color */}
        <div className="dropdown-wrapper">
          <button className="toolbar-btn" onClick={() => toggleDropdown("color")} title="Text Color">
            <i className="fas fa-font" style={{ color: selectedColor }}></i>
          </button>
          {showDropdowns.color && (
            <div className="dropdown-menu">
              <div className="color-grid">
                {colors.map((color) => (
                  <div key={color} className="color-item" style={{ backgroundColor: color }}
                    onClick={() => { setSelectedColor(color); execCmd("foreColor", color); closeDropdowns(); }}></div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Highlight Color */}
        <div className="dropdown-wrapper">
          <button className="toolbar-btn" onClick={() => toggleDropdown("highlight")} title="Highlight">
            <span style={{
              display: "inline-block",
              width: "1rem",
              height: "1rem",
              backgroundColor: selectedHighlight,
              border: "1px solid #ccc",
              marginRight: "0.3rem"
            }}></span>
            <i className="fas fa-highlighter"></i>
          </button>
          {showDropdowns.highlight && (
            <div className="dropdown-menu">
              <div className="color-grid">
                {colors.map((color) => (
                  <div key={color} className="color-item" style={{ backgroundColor: color }}
                    onClick={() => { 
                      setSelectedHighlight(color); 
                      execCmd("backColor", color);  // use backColor for highlight
                      closeDropdowns(); 
                    }}></div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="toolbar-separator"></div>

        <button className={`toolbar-btn ${activeFormats.ul ? "active" : ""}`} onClick={() => execCmd("insertUnorderedList")}><i className="fas fa-list-ul"></i></button>
        <button className={`toolbar-btn ${activeFormats.ol ? "active" : ""}`} onClick={() => execCmd("insertOrderedList")}><i className="fas fa-list-ol"></i></button>

        <div className="toolbar-separator"></div>

        {/* Alignment */}
        <button className={`toolbar-btn ${align === "left" ? "active" : ""}`} onClick={() => execCmd("justifyLeft")}><i className="fas fa-align-left"></i></button>
        <button className={`toolbar-btn ${align === "center" ? "active" : ""}`} onClick={() => execCmd("justifyCenter")}><i className="fas fa-align-center"></i></button>
        <button className={`toolbar-btn ${align === "right" ? "active" : ""}`} onClick={() => execCmd("justifyRight")}><i className="fas fa-align-right"></i></button>

        {allowImages && <button className="toolbar-btn" onClick={insertImage}><i className="fas fa-image"></i></button>}

        <button className="toolbar-btn" onClick={() => execCmd("undo")}><i className="fas fa-undo"></i></button>
        <button className="toolbar-btn" onClick={() => execCmd("redo")}><i className="fas fa-redo"></i></button>
      </div>

      <div ref={editorRef} className="editor-content" style={{ minHeight: height }}
        data-placeholder={placeholder} contentEditable={!disabled}
        onInput={handleChange} onPaste={handlePaste}
        onMouseUp={updateFormats} onKeyUp={updateFormats}
        suppressContentEditableWarning={true}></div>
    </div>
  );
};

export default GoogleDocsEditor;
